/* eslint-env browser */
/* tslint:disable:max-line-length */

// Vendor
import { inject } from 'mobx-react';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import * as React from 'react';

// Bernie
import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { AlterLocalizationProvider } from 'bernie-alter';
import { AnalyticsStore, PageStore, RouteStore } from 'bernie-core';
import { Context } from 'bernie-context';
import { ErrorBoundary } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

// Stores
import { ContextPair, MultiContextProvider, SearchCriteriaContext } from 'stores/contexts';
import { MultiItemStore } from 'stores/multi-item-store';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { VirtualAgentControlStore } from 'stores/virtual-agent-control-store';

// Components
import { Footer } from '@gc/global-components';
// Controllers
import { HotelHomeController } from 'controllers/hotel/hotel-home-controller';
import { AnimatedProgressIndicator } from 'components/common/animated-progress-indicator';
import { BodyRegion } from './body-region';
import { Header } from 'components/common/header/header';
import { HeaderRegion } from './header-region';
import { InfositeController } from 'controllers/hotel/infosite-controller';
import { MainRegion } from './main-region';
import { MesoScripts } from 'components/meso/meso-scripts';
import { NavigationRegion } from './navigation-region';
import { Notification } from 'components/common/notification';
import { PwaController } from 'controllers/admin/pwa-controller';
import { SearchController } from 'controllers/hotel/search-controller';
import { SideRailRegion } from './side-rail-region';
import { ToolbarRegion } from './toolbar-region';
import * as invariant from 'invariant';

const toolbarTypes = {
  FIXED_FLEXIBLE_WATERFALL: 'pwa-toolbar__fixed-flexible-waterfall pwa-toolbar',
  NORMAL: 'pwa-toolbar__normal pwa-toolbar',
  TRANSPARENT_OVERLAY_WITH_HEADER: 'pwa-toolbar__transparent-overlay-with-header pwa-toolbar',
  TRANSPARENT_OVERLAY: 'pwa-toolbar__transparent-overlay pwa-toolbar',
  TOOLBAR_WHITE_BACKGROUND: 'pwa-toolbar-white xl-l-padding-four'
};

export const sideRailTypes = {
  NEXT_TO_MAIN_AND_TOOLBAR: 'NEXT_TO_MAIN_AND_TOOLBAR',
  NEXT_TO_MAIN: 'NEXT_TO_MAIN',
  NONE: 'NONE'
};

export interface MasterPageProps extends LocalizedComponentProps {
  analytics?: AnalyticsStore;
  className?: string;
  context?: Context;
  dataStid?: string;
  page?: PageStore;
  pageHeaderClassName?: string;
  route?: HotelHomeController | InfositeController | PwaController | SearchController;
  routeStore?: RouteStore;
  searchCriteria?: SearchCriteriaModel;
  sideRailType?: string;
  toolbarType?: string;
  multiItem?: MultiItemStore;
  flexLayoutSplitView?: boolean;
  pageHeader?: string;
  virtualAgentControlStore?: VirtualAgentControlStore;
}

/**
 * MasterPage - stateless component for defining a reusable page structure with specific regions of content
 *
 * @param this.props {object} - the standard this.props object passed by React
 * @returns {string} - populated HTML of all applicable regions and wrapped in a div
 */
@withLocalization
@inject('analytics', 'context', 'page', 'routeStore', 'multiItem', 'virtualAgentControlStore')
export class MasterPage extends React.Component<MasterPageProps> {
  public static sideRailTypes = sideRailTypes;
  public static toolbarTypes = toolbarTypes;

  public static defaultProps = {
    sideRailType: sideRailTypes.NONE,
    toolbarType: toolbarTypes.NORMAL,
    l10n: null,
    flexLayoutSplitView: false
  };

  constructor(props) {
    super(props);

    if (this.props.analytics && this.props.route && this.props.route.routeData) {
      const routeData = this.props.route.routeData;

      this.props.analytics.setPageNamespace(routeData.rfrrNamespace || '');
      // nested the above if statement instead of appending to outer ones so we can start handling other routeData here

      if (this.props.page && !this.props.page.pageName) {
        this.props.page.pageName = routeData.pageName || 'page.unknown';
      }

      this.props.routeStore.logoutPath = '/Hotels';
    }

    this.setPageTitle = this.setPageTitle.bind(this);
  }

  public componentDidMount() {
    this.setPageTitle(this.props.page && this.props.page.title);
  }

  public componentWillUpdate(nextProps) {
    this.setPageTitle(nextProps.page && nextProps.page.title);
  }

  public setPageTitle(title) {
    document.title = title || this.props.l10n.formatText('common.defaultPageTitle');
  }

  public render() {
    const {
      context,
      dataStid,
      pageHeaderClassName,
      searchCriteria,
      flexLayoutSplitView,
      pageHeader,
      virtualAgentControlStore
    } = this.props;

    const headerDefault = <Header className={pageHeaderClassName} />;

    let headerContent = null;
    let mainContent: React.ReactElement<{}> | null = null;
    let sideRailContent: React.ReactElement<{}> | null = null;
    let toolbarContent: React.ReactElement<{}> | null = null;
    let navigationContent: React.ReactElement<{}> | null = null;

    React.Children.forEach(this.props.children, child => {
      if (React.isValidElement(child)) {
        invariant(
          child.type === HeaderRegion ||
            child.type === MainRegion ||
            child.type === SideRailRegion ||
            child.type === ToolbarRegion ||
            child.type === NavigationRegion,
          `MasterPage: child type "${(child.type && !(typeof child.type === 'string') && child.type.name) ||
            child.type}" is invalid. Can only contain children of type ["HeaderRegion", "MainRegion", "ToolbarRegion", "NavigationRegion"]`
        );

        switch (child.type) {
          case HeaderRegion:
            invariant(headerContent === null, 'MasterPage: can only contain one child of type "HeaderRegion"');
            headerContent = child;
            break;
          case MainRegion:
            invariant(mainContent === null, 'MasterPage: can only contain one child of type "MainRegion"');
            mainContent = child;
            break;
          case SideRailRegion:
            invariant(sideRailContent === null, 'MasterPage: can only contain one child of type "SideRailRegion"');
            sideRailContent = child;
            break;
          case ToolbarRegion:
            invariant(toolbarContent === null, 'MasterPage: can only contain one child of type "ToolbarRegion"');
            toolbarContent = child;
            break;
          case NavigationRegion:
            invariant(navigationContent === null, 'MasterPage: can only contain one child of type "NavigationRegion"');
            navigationContent = child;
            break;
        }
      }
    });

    const headerClassName = ['pwa-toolbar', 'pwa-toolbar-header', this.props.toolbarType].filter(val => val).join(' ');
    const headerContentRegion = headerContent && <header className={headerClassName}>{headerContent}</header>;

    const contextPairs: ContextPair[] = [{ context: SearchCriteriaContext, value: searchCriteria }];

    const pageContainerRegionProps = {
      dataStid,
      headerContentRegion,
      headerDefault,
      flexLayoutSplitView,
      toolbarType: this.props.toolbarType,
      sideRailType: this.props.sideRailType,
      toolbarContent,
      mainContent,
      sideRailContent,
      navigationContent,
      pageHeader,
      context
    };

    return (
      <AlterLocalizationProvider>
        <MultiContextProvider contextPairs={contextPairs}>
          <AbacusExperiment name="Lshop_Vac_Chatbot">
            <AbacusControl />
            <AbacusVariant bucket={1}>
              {virtualAgentControlStore && virtualAgentControlStore.javascriptUrl && (
                <script type="text/javascript" src={virtualAgentControlStore.javascriptUrl} />
              )}
            </AbacusVariant>
          </AbacusExperiment>

          {this.props.page && this.props.page.title && !this.props.page.seo && (
            <h1 className="is-visually-hidden">{this.props.page.title}</h1>
          )}

          <ErrorBoundary label="hotel.masterPage.renderToNodeStreamTest">
            <AbacusExperiment name="PWA_PAGE_RENDER_TO_NODE_STREAM">
              <AbacusControl />
              <AbacusVariant bucket={1} />
            </AbacusExperiment>
          </ErrorBoundary>

          <AbacusExperiment name="MeSo_PWA_SideRail_Placement">
            <AbacusControl>
              <PageContainerRegion {...pageContainerRegionProps} containerClass={`page-container ${this.props.className}`} />
            </AbacusControl>
            <AbacusVariant bucket={1}>
              <PageContainerRegion
                {...pageContainerRegionProps}
                containerClass={`page-container ${this.props.className} uitk-flex-align-items-center`}
              />
            </AbacusVariant>
          </AbacusExperiment>

          <MesoScripts />
        </MultiContextProvider>
      </AlterLocalizationProvider>
    );
  }
}

const PageContainerRegion = ({
  dataStid,
  headerContentRegion,
  headerDefault,
  flexLayoutSplitView,
  toolbarType,
  sideRailType,
  toolbarContent,
  mainContent,
  sideRailContent,
  navigationContent,
  pageHeader,
  context,
  containerClass
}) => (
  <div className={containerClass} data-stid={dataStid}>
    <ErrorBoundary label="hotel.masterPage.header">{headerContentRegion || headerDefault}</ErrorBoundary>
    {flexLayoutSplitView && (
      <UitkLayoutFlex alignItems="start" className={toolbarType}>
        <UitkLayoutFlexItem minWidth={992}>
          <div>
            <ErrorBoundary label="hotel.masterPage.toolbar">{toolbarContent}</ErrorBoundary>
          </div>
        </UitkLayoutFlexItem>
      </UitkLayoutFlex>
    )}
    <BodyRegion
      mainContent={mainContent}
      toolbarContent={!flexLayoutSplitView ? toolbarContent : null}
      sideRailContent={sideRailContent}
      toolbarType={toolbarType}
      sideRailType={sideRailType}
      navigationContent={navigationContent}
      flexLayoutSplitView={flexLayoutSplitView}
      pageHeader={pageHeader}
    />
    {!flexLayoutSplitView && (
      <div className="uitk-grid">
        <Footer context={context} />
      </div>
    )}
    <AnimatedProgressIndicator />
    <Notification />
  </div>
);
